import { RouterProvider, createBrowserHistory, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { type Root, createRoot } from 'react-dom/client';
import { DEMO_IN_IFRAME } from './lib/demo';
import { queryClient } from './lib/query-client';
import {
  RELOAD_PARAM,
  isRouterInternalError,
  reloadForStaleChunk,
  staleChunkReason,
} from './lib/stale-chunk';
import { routeTree } from './routeTree.gen';
import './lib/theme'; // side effect: applies stored theme + tracks system changes
import './styles/globals.css';

// Deploy-cutover recovery. When a new deploy replaces the hashed route
// chunks this tab was about to lazy-load, Vite's module-preload helper
// dispatches `vite:preloadError`. Reload once to pull the fresh asset
// graph instead of surfacing a "failed to import module" crash.
// `preventDefault` stops Vite from re-throwing; the boundaries below
// (ErrorBoundary / AppCrashScreen) are the fallback if the event never
// fires for a given import. See lib/stale-chunk.ts.
if (typeof window !== 'undefined') {
  // Strip the stale-chunk cache-buster before the router reads the URL,
  // so a recovery reload (lib/stale-chunk.ts) doesn't leave a visible
  // `?__cwreload=…` in the address bar. The fresh fetch it forced has
  // already happened by now — this is just cosmetic cleanup.
  const bootUrl = new URL(window.location.href);
  if (bootUrl.searchParams.has(RELOAD_PARAM)) {
    bootUrl.searchParams.delete(RELOAD_PARAM);
    window.history.replaceState(window.history.state, '', bootUrl.toString());
  }

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadForStaleChunk(staleChunkReason(event.payload));
  });
  // Router-internal preload race during a cutover. Symptom: a hover-
  // triggered route preload runs through `preloadRouteComponents` and
  // hits a route whose `options` isn't yet fully initialised — surfaces
  // as an unhandled `Promise.all` rejection with a TypeError stack
  // through `vendor-tanstack-*.js`. The chunks loaded fine; the router's
  // internal state was just half-built. Reload once to get a clean boot.
  // `isRouterInternalError`'s stack-origin gate keeps this from firing
  // on app-code TypeErrors that happen to share the message shape.
  window.addEventListener('unhandledrejection', (event) => {
    if (isRouterInternalError(event.reason)) {
      event.preventDefault();
      reloadForStaleChunk(staleChunkReason(event.reason));
    }
  });
}

// First-party pixel is installed via a `<script src>` tag in index.html
// (same snippet customers paste). Stack-aware site_id + endpoint come
// from `.env` written by deploy-dashboard-v2.yml. No JS bridge needed
// here — the IIFE boots itself and reads its config from `data-*`
// attributes on its own script tag.

// DevTools easter egg. Shape is whitespace-sensitive — do not reformat the
// template literal below. Bolt art is leading-whitespace only (no trailing
// padding required to hold the shape).
console.log(
  `
                                      --.
                                    .-::::-
                                   :::::+::
                  ..-.           .:::+:::-.
                .:+::::-       .-:+:::::-
               .:+:++:+::-    -:::++::-.
             .-:+:++:::+++:-.::++++:+-
            .:+::+::+++++::::++::++-.
           -+::+:++- .::++:::++++:.
         .::++:++:.    .::=:+::+:
        -:+++++::        -:+++:-
       :++++++:.           ...
      :++=+++-.
      :=+=++-
       .-:..

⚡ clickweave — built for speed.
`,
);

// `queryClient` (imported from `lib/query-client.ts`) is threaded into
// the router context so route loaders (`context.queryClient.
// ensureQueryData(...)`) — which run outside the React tree — hit the
// same cache RootShell provides via QueryClientProvider. It must NOT
// live in this file: `routes/__root.tsx` also needs it, and importing
// it from here made the entry and the route tree circular.
// Marketing iframes (www's hero frame + its mid-page embed slices) keep
// their navigation out of the host page's Back button. An iframe shares
// the top-level session history, so a `pushState` in here — every tab,
// metric toggle and filter the demo offers — leaves an entry a visitor
// has to press Back through before they can leave clickweave.co. Swap
// push for replace: the frame navigates exactly as before, the parent's
// history stays where the visitor left it. Standalone demo.clickweave.co
// tabs keep normal history (Back means Back there).
const history = createBrowserHistory();
if (DEMO_IN_IFRAME) {
  history.push = (path, state, navigateOpts) => history.replace(path, state, navigateOpts);
}

const router = createRouter({
  routeTree,
  history,
  defaultPreload: 'intent',
  // Stale time on a preloaded route's loader-fetched data. 30s lines
  // up with how long a user typically takes between hover and click —
  // longer and we'd serve genuinely stale data, shorter and the
  // preload becomes useless because it's already expired by the time
  // the click lands.
  defaultPreloadStaleTime: 30_000,
  context: { queryClient },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root') as (HTMLElement & { __cwReactRoot?: Root }) | null;
if (!rootEl) throw new Error('#root not found');

// Reuse a root stashed on the container if this entry re-executes
// (Vite dev re-runs it under a `?t=` URL when an HMR invalidation
// reaches the entry). A second createRoot() on the same container
// kills React's event delegation until a hard reload.
const root = rootEl.__cwReactRoot ?? createRoot(rootEl);
rootEl.__cwReactRoot = root;
root.render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
